import React, { useState, useEffect } from 'react';
import {
  Container, Box, Typography, Paper, Grid, TextField, Button, Alert, Divider, Chip,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import axios from 'axios';
import SidebarLayout from '../components/SidebarLayout';

interface BiliCfg {
  barrageInterval: number;
  barrageRounds: number;
  commentInterval: number;
  commentRounds: number;
  playMinSeconds: number;
  playCooldownSeconds: number;
  barrageUrl: string;
  commentUrl: string;
}

const DEFAULT_CONFIG: BiliCfg = {
  barrageInterval: 60,
  barrageRounds: 1,
  commentInterval: 60,
  commentRounds: 1,
  playMinSeconds: 20,
  playCooldownSeconds: 300,
  barrageUrl: '',
  commentUrl: '',
};

const BiliConfig = () => {
  const [config, setConfig] = useState<BiliCfg>(DEFAULT_CONFIG);
  const [form, setForm] = useState<BiliCfg>(DEFAULT_CONFIG);
  const [saveMsg, setSaveMsg] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchConfig = async () => {
    const resp = await axios.get('/api/bili/config');
    if (resp.data.success) {
      const cfg = { ...DEFAULT_CONFIG, ...resp.data.config };
      setConfig(cfg);
      setForm(cfg);
    }
  };

  useEffect(() => { fetchConfig().catch(console.error); }, []);

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.put('/api/bili/config', form);
      setSaveMsg('配置已保存');
      fetchConfig();
    } catch (e: any) {
      setSaveMsg(e.response?.data?.message || '保存失败');
    } finally {
      setLoading(false);
      setTimeout(() => setSaveMsg(''), 4000);
    }
  };

  return (
    <SidebarLayout>
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <SettingsIcon color="primary" />
          <Typography variant="h5">B站发送 / 播放规则配置</Typography>
        </Box>
        <Paper sx={{ p: 2.5, mb: 3, bgcolor: '#f5f9ff' }}>
          <Typography variant="subtitle2" color="primary" sx={{ mb: 1.5 }}>当前生效配置</Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Chip label={`弹幕间隔 ${config.barrageInterval}s`} size="small" variant="outlined" />
            <Chip label={`弹幕轮次 ${config.barrageRounds}`} size="small" variant="outlined" />
            <Chip label={`评论间隔 ${config.commentInterval}s`} size="small" variant="outlined" />
            <Chip label={`评论轮次 ${config.commentRounds}`} size="small" variant="outlined" />
            <Chip label={`播放保底 ${config.playMinSeconds}s`} size="small" variant="outlined" />
            <Chip label={`一轮总时长 ${config.playCooldownSeconds}s`} size="small" variant="outlined" />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            播放量规则：视频数 ≤9 时，按「一轮总时长」平分到每个视频；超过 9 个时每个按「保底秒数」播放。
            统计小视频请在「用户管理」或边端（总控/单独）按用户配置，不在此全局设置。
          </Typography>
          {(config.barrageUrl || config.commentUrl) && (
            <Box sx={{ mt: 1.5 }}>
              {config.barrageUrl && (
                <Typography variant="body2" color="text.secondary">弹幕地址：{config.barrageUrl}</Typography>
              )}
              {config.commentUrl && (
                <Typography variant="body2" color="text.secondary">评论地址：{config.commentUrl}</Typography>
              )}
            </Box>
          )}
        </Paper>
        <Paper sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>修改配置</Typography>
          <Divider sx={{ mb: 3 }} />
          {saveMsg && <Alert severity={saveMsg.includes('失败') ? 'error' : 'success'} sx={{ mb: 2 }}>{saveMsg}</Alert>}
          <Grid container spacing={3}>
            {([
              ['barrageInterval', '弹幕间隔（秒）'],
              ['barrageRounds', '弹幕轮次'],
              ['commentInterval', '评论间隔（秒）'],
              ['commentRounds', '评论轮次'],
              ['playMinSeconds', '单视频保底秒数（超过9个时）'],
              ['playCooldownSeconds', '一轮循环总时长（秒，≤9个时平分）'],
            ] as const).map(([key, label]) => (
              <Grid item xs={12} sm={6} key={key}>
                <TextField fullWidth type="number" label={label} value={form[key]}
                  onChange={(e) => setForm({ ...form, [key]: parseInt(e.target.value, 10) || 0 })} />
              </Grid>
            ))}
            <Grid item xs={12}>
              <TextField fullWidth label="弹幕目标页地址" value={form.barrageUrl}
                placeholder="完整链接或 BV 短号，如 BV1vvKw6UEx8"
                onChange={(e) => setForm({ ...form, barrageUrl: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="评论目标页地址" value={form.commentUrl}
                placeholder="完整链接或 BV 短号，留空则用当前页"
                onChange={(e) => setForm({ ...form, commentUrl: e.target.value })}
                helperText="与弹幕地址不同时，同一窗口不可同时开弹幕+评论" />
            </Grid>
          </Grid>
          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button variant="contained" onClick={handleSave} disabled={loading}>保存配置</Button>
            <Button variant="outlined" onClick={() => setForm(config)}>重置</Button>
          </Box>
        </Paper>
      </Container>
    </SidebarLayout>
  );
};

export default BiliConfig;
